import React, {useRef, useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {Box, Fade, Flex, Text, useMultiStyleConfig} from '@chakra-ui/react'
import ProductTileBundle from './partials/product-tile-bundle'
import AddToCartButton from '../add-to-cart-button'
import CurrentPrice from '../display-price/current-price'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import {FormattedMessage, useIntl} from 'react-intl'
import {API_ERROR_MESSAGE} from '../../constants'
import {remCalc} from '../../theme/_utils'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../custom-api/layers/default'
import {getPriceDataUseProduct} from '../../utils/product-utils'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {chunk} from 'lodash'
import {BRAND_KEY} from '../../brand'

const Bundle = ({title, currentPageProduct}) => {
    const styles = useMultiStyleConfig('Bundle')
    const {currency} = useCurrency()
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')

    const [showErrorMessage, setShowErrorMessage] = useState(0)
    const errorContainerRef = useRef(null)
    const {formatMessage} = useIntl()

    const currentPageProductId = currentPageProduct?.id || currentPageProduct?.productId
    {
        /* Calculate the total price of the bundle */
    }
    const bundleTotalPrice =
        currentPageProduct?.c_bundleProductsInfo?.find((child) => child.totalBundlePrice)
            ?.totalBundlePrice || 0

    {
        /* Retrieve the bundle id from one of the products */
    }
    const bundleId = currentPageProduct?.c_bundleProductsInfo.find(
        (item) => item.productSetID,
    )?.productSetID

    const bundleData = []
    currentPageProduct?.c_bundleProductsInfo.forEach((item) => {
        if (!item.totalBundlePrice) {
            bundleData.push(item)
        }
    })

    const bundleCount = currentPageProduct?.c_bundleProductsInfo?.length - 1 || 0
    const lgWidthBundle =
        BRAND_KEY === 'kenwood'
            ? `calc(5.2% + (23.7% * ${bundleCount}))`
            : `calc(25% * ${bundleCount})`

    // retrieve bundle data for GTM event, excluding the current page product
    const {addLayer} = useGoogleTagManager()
    const {data: bundleProductsData, isLoading} = useProducts(
        {
            parameters: {
                ids: bundleData.map((p) => p.productId).filter((id) => id !== currentPageProductId),
                expand: ['availability', 'images', 'prices', 'promotions'],
            },
            headers: {
                'x-debug': 'bundle-index',
            },
        },
        {
            enabled: bundleData?.length > 0,
        },
    )
    useEffect(() => {
        if (bundleProductsData && !isLoading) {
            addLayer(polish_ecommerce_gtm_event.bind(this))
            chunk(bundleProductsData.data || [], 10).forEach((bundleChunk, index) => {
                addLayer(
                    item_gtm_event.bind(this, {
                        event: 'view_item_list',
                        bundle_id: bundleId,
                        bundle: 'true',
                        item_list_name: 'bundle',
                        items: bundleChunk?.map((product, i) => ({
                            item_name: product?.c_defaultName,
                            item_id: product?.productId || product?.id,
                            item_category: product?.c_defaultCategoryName,
                            item_variant: product?.c_subModelIdentifier,
                            price: +(product?.price / (1 + product?.c_taxRate / 100)).toFixed(2),
                            fullprice: getPriceDataUseProduct(product)?.isOnSale
                                ? 'reduced'
                                : 'fullprice',
                            rating_points: '',
                            erp_category: product?.c_materialGroup || product?.c_MaterialGroup,
                            prdha_category: product?.c_prdhaCategory || product?.c_prdha_category,
                            index: i + 1 + index * 10,
                            stock_status:
                                product?.inventory?.stockLevel > 0 ? 'in-stock' : 'out-of-stock',
                        })),
                    }),
                )
            })
        }
    }, [bundleProductsData, isLoading])

    const addToCartCallback = (itemsAdded) => {
        if (itemsAdded) {
            addLayer(
                item_gtm_event.bind(this, {
                    event: 'add_to_cart',
                    bundle_id: bundleId,
                    bundle: 'true',
                    item_list_name: 'bundle',
                    items: itemsAdded.map((item, index) => {
                        var itemId = item.product?.productId || item?.id
                        var bundleProduct =
                            itemId === currentPageProductId
                                ? currentPageProduct
                                : (bundleProductsData?.data || []).find(
                                      (p) => p.id === itemId || p.productId === itemId,
                                  )
                        return {
                            item_name: bundleProduct?.c_defaultName,
                            item_id: bundleProduct?.productId || bundleProduct?.id,
                            item_category: bundleProduct?.c_defaultCategoryName,
                            item_variant: bundleProduct?.c_subModelIdentifier,
                            price: (
                                bundleProduct?.price /
                                (1 + bundleProduct?.c_taxRate / 100)
                            ).toFixed(2),
                            fullprice: getPriceDataUseProduct(bundleProduct)?.isOnSale
                                ? 'reduced'
                                : 'fullprice',
                            rating_points: '',
                            erp_category:
                                bundleProduct?.c_materialGroup || bundleProduct?.c_MaterialGroup,
                            prdha_category:
                                bundleProduct?.c_prdhaCategory || bundleProduct?.c_prdha_category,
                            index: index + 1,
                            stock_status:
                                bundleProduct?.stockLevel !== null && bundleProduct?.stockLevel > 0
                                    ? 'in-stock'
                                    : 'out-of-stock',
                            quantity: item.quantity,
                        }
                    }),
                }),
            )
        }
    }

    return (
        <Flex {...styles.outerContainer} data-testid="bundle-product-view">
            <Text {...styles.headerLabel} data-testid="bundle-product-title">
                {title}
            </Text>
            <Flex {...styles.innerContainer}>
                <Flex sx={styles.productsContainer} data-testid="bundle-product-container">
                    {bundleData.map((item, i) => {
                        return (
                            <ProductTileBundle
                                key={i}
                                product={item}
                                firstElement={i === 0}
                                lastElement={i === bundleData.length - 1}
                            />
                        )
                    })}
                </Flex>
            </Flex>
            <Flex
                {...styles.addCartButtonContainer}
                sx={{
                    width: {
                        base: '100%',
                        lg: lgWidthBundle,
                    },
                }}
            >
                <Flex {...styles.subtotalPrice}>
                    <FormattedMessage defaultMessage="Subtotal:" id="bundle_subtotal.text" />
                    <CurrentPrice
                        price={bundleTotalPrice}
                        currency={currency}
                        sx={{
                            whiteSpace: 'nowrap',
                            textStyle: 'head-small',
                            marginLeft: {base: 0, lg: remCalc(16)},
                            marginRight: {base: 0, lg: remCalc(16)},
                        }}
                    />
                </Flex>
                {!notShoppableCountry && (
                    <Flex {...styles.addCartButton} data-testid="bundle-product-add-to-cart-button">
                        <AddToCartButton
                            product={bundleData}
                            setShowErrorMessage={setShowErrorMessage}
                            errorContainerRef={errorContainerRef}
                            showUniversalFeedback={true}
                            bundleID={bundleId}
                            callback={addToCartCallback}
                        />
                    </Flex>
                )}
            </Flex>
            <Box ref={errorContainerRef}>
                {showErrorMessage ? (
                    <Fade in={true}>
                        <Text color="text.danger" fontWeight="semibold" marginBottom={8}>
                            {formatMessage(API_ERROR_MESSAGE)}
                        </Text>
                    </Fade>
                ) : (
                    <></>
                )}
            </Box>
        </Flex>
    )
}

Bundle.propTypes = {
    title: PropTypes.node,
    currentPageProduct: PropTypes.object,
}

export default Bundle
